import React from 'react'
import { RouteChildrenProps } from 'react-router'
import styled from '@emotion/styled'
import { Auth } from 'aws-amplify'
import * as Sentry from '@sentry/browser'
import { CognitoUser, CognitoUserAttribute } from 'amazon-cognito-identity-js'
import { Link, NavLink } from 'react-router-dom'
import { Typography } from '@dw/helix'
import './Navmenu.scss'
import { HasGroup, RefreshAuthData } from '../../../contexts'
import pageRoleGroups from '../Roles'
import { Dropdown } from '../../../components'

interface Props extends RouteChildrenProps {
  refreshAuthData: RefreshAuthData
  userData: CognitoUser
  hasGroup: HasGroup
  isScanPageNew: boolean
  toggleScanPage: (newValue?: boolean) => void
}

interface State {
  isProfileMenuOpen: boolean
  isMobileMenuOpen: boolean
  userAttributes: {
    email: string
    'custom:username': string
    'custom:companyName'?: string
  }
}

const SerialNumbersMenu = styled.ul`
  position: absolute;
  top: 100%;
  right: 0;
  background: rgb(39, 55, 81);
  border-radius: 0;
  width: 290px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  text-align: right;
  list-style: none;
  li {
    a {
      color: rgb(255, 255, 255);
      padding: 10px 0;
      display: flex;
      justify-content: flex-end;
    }

    &:first-of-type {
      a {
        padding-top: 0;
      }
    }
    &:last-of-type {
      a {
        padding-bottom: 0;
      }
    }
  }
`
const DropdownWrapper = styled.div`
  display: flex;
  height: 50px;
  button {
    color: #fff;
    padding: 15px 10px;
    font-size: 13px;
    letter-spacing: 1.4px;
    background-color: transparent;
    border: 0;
    text-transform: uppercase;
    width: 100%;
    text-align: left;
    &:focus {
      outline: none;
      border: none;
    }
  }
`

export class Navmenu extends React.Component<Props, State> {
  dropdownRef: HTMLButtonElement | null = null

  state: State = {
    isProfileMenuOpen: false,
    isMobileMenuOpen: false,
    userAttributes: {
      email: '',
      'custom:username': '',
      'custom:companyName': undefined,
    },
  }

  componentDidMount() {
    this.props.userData.getUserAttributes((e?: Error, attributes?: CognitoUserAttribute[]): void => {
      if (!attributes) {
        return
      }
      const userAttributes = attributes.reduce((acc: any, attribute) => {
        acc[attribute.getName()] = attribute.getValue()
        return acc
      }, {})
      this.setState({ userAttributes })
    })
    document.addEventListener('click', this.handleClickOutsideProfileMenu)
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutsideProfileMenu)
  }

  handleClickOnProfileMenu = () => {
    this.setState({
      isProfileMenuOpen: !this.state.isProfileMenuOpen,
    })
  }

  handleClickOutsideProfileMenu = (event: MouseEvent) => {
    if (this.dropdownRef && !this.dropdownRef.contains(event.target as HTMLElement)) {
      this.setState({
        isProfileMenuOpen: false,
      })
    }
  }

  toggleMobileMenu = () => {
    this.setState({
      isMobileMenuOpen: !this.state.isMobileMenuOpen,
    })
  }

  render() {
    const { isScanPageNew, toggleScanPage, location, history, hasGroup } = this.props

    return (
      <div data-component="navmenu-component">
        <div className="navbar navbar-default navbar-fixed-top">
          <div className="container">
            <div className="navbar-header">
              <button type="button" className="navbar-toggle collapsed" onClick={this.toggleMobileMenu}>
                <span className="sr-only">Toggle navigation</span>
                <span className="icon-bar" />
                <span className="icon-bar" />
                <span className="icon-bar" />
              </button>
              {location.pathname === '/manual' ? (
                <Link to="/" className="navbar-brand">
                  Item Tracing
                  <span> Manual</span>
                </Link>
              ) : (
                <Link to="/" className="navbar-brand">
                  Item
                  <span> Tracing</span>
                </Link>
              )}
            </div>

            <div
              id="navbar"
              className={`navbar-collapse collapse ${this.state.isMobileMenuOpen ? 'in' : ''}`}
              onClick={this.toggleMobileMenu}
            >
              {location.pathname === '/manual' ? (
                <ul className="nav navbar-nav navbar-right">
                  <li className="menu-tabs">
                    <button onClick={history.goBack}>
                      Back <i className="glyphicon glyphicon-arrow-right" />
                    </button>
                  </li>
                </ul>
              ) : (
                <>
                  <ul className="nav navbar-nav">
                    {hasGroup(pageRoleGroups.scan) && (
                      <li className="menu-tabs">
                        <NavLink to="/scan">Scan</NavLink>
                      </li>
                    )}
                    {hasGroup(pageRoleGroups.upload) && (
                      <li className="menu-tabs">
                        <NavLink to="/upload">Upload files</NavLink>
                      </li>
                    )}
                    {hasGroup(pageRoleGroups.trace) && (
                      <li className="menu-tabs">
                        <NavLink to="/trace">Serial/Order Search</NavLink>
                      </li>
                    )}
                    {hasGroup(pageRoleGroups['batch-numbers']) && (
                      <li className="menu-tabs">
                        <NavLink to="/batch-numbers">Batch Search</NavLink>
                      </li>
                    )}
                    {hasGroup(pageRoleGroups['authentic-check']) && (
                      <li className="menu-tabs">
                        <NavLink to="/authentic-check">Authentic Check</NavLink>
                      </li>
                    )}
                    {hasGroup(pageRoleGroups['generate-serials']) && (
                      <li className="menu-tabs">
                        <Dropdown shouldCloseOnOutside={true}>
                          {({ dropdownOpened, toggleDropdown, closeDropdown, nodeRef }) => (
                            <DropdownWrapper ref={nodeRef}>
                              <button type="button" onClick={toggleDropdown}>
                                Generate serial numbers
                              </button>
                              {dropdownOpened && (
                                <SerialNumbersMenu>
                                  <li>
                                    <NavLink to="/generate-serials" onClick={closeDropdown}>
                                      <Typography type="label">Generate serial numbers</Typography>
                                    </NavLink>
                                  </li>
                                  <li>
                                    <NavLink to="/manage-codes" onClick={closeDropdown}>
                                      <Typography type="label">Manage batch/serial codes</Typography>
                                    </NavLink>
                                  </li>
                                </SerialNumbersMenu>
                              )}
                            </DropdownWrapper>
                          )}
                        </Dropdown>
                      </li>
                    )}
                    <li className="menu-tabs hidden-lg">
                      <button onClick={this.handleLogout}>Logout</button>
                    </li>
                    {!hasGroup(pageRoleGroups.manual) && (
                      <li className="menu-tabs hidden-lg">
                        <NavLink to="/manual">
                          Help <span className="glyphicon glyphicon-question-sign" />
                        </NavLink>
                      </li>
                    )}
                  </ul>
                  <ul className="nav navbar-nav navbar-right hidden-xs hidden-sm hidden-md">
                    <li className={this.state.isProfileMenuOpen ? 'dropdown open' : 'dropdown'}>
                      <button
                        id="simple-dropdown"
                        ref={node => {
                          this.dropdownRef = node
                        }}
                        onClick={this.handleClickOnProfileMenu}
                      >
                        {this.state.userAttributes['custom:username']}
                        <span className="glyphicon glyphicon-user" />
                      </button>
                      <ul className="dropdown-menu">
                        <li>
                          <span className="dropdown-item info-item">
                            {this.state.userAttributes.email}
                            {this.state.userAttributes['custom:companyName'] && (
                              <>
                                <br /> {this.state.userAttributes['custom:companyName']}
                              </>
                            )}
                          </span>
                        </li>
                        {hasGroup(pageRoleGroups.newScan) && (
                          <li>
                            <button className="dropdown-item scan-page-switcher" onClick={() => toggleScanPage()}>
                              Switch to {isScanPageNew ? 'old' : 'new'} scan page
                            </button>
                          </li>
                        )}
                        <li>
                          <hr />
                        </li>
                        <li>
                          <button className="dropdown-item" onClick={this.handleLogout}>
                            Logout
                          </button>
                        </li>
                      </ul>
                    </li>
                    {!hasGroup(pageRoleGroups.manual) && (
                      <li>
                        <NavLink to="/manual">
                          Help <span className="glyphicon glyphicon-question-sign" />
                        </NavLink>
                      </li>
                    )}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  private handleLogout = (): Promise<any> => {
    return Auth.signOut().then(() => {
      this.props.refreshAuthData()

      Sentry.withScope(scope => {
        scope.setLevel(Sentry.Severity.Debug)
        scope.setTag('feature', 'logout')
        Sentry.captureMessage('Logout')
      })

      Sentry.configureScope(scope => {
        scope.setUser({})
      })
    })
  }
}
